import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, ShieldAlert, WifiOff } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { authApi } from "@/api";
import { Button } from "@/components/ui/button";
import { PageLoader } from "@/components/shared";
import { AuthNotice } from "@/layout/auth-notice";
import { QK, canAccessAdmin } from "@/constants";
import { env } from "@/configs/env";
import { isApiError, onSessionLost } from "@/configs/request";
import { useSessionStore } from "@/store";
import type { ChildrenProps } from "@/types/props";

/**
 * Ikki bosqichli qorovul: avval sessiya bormi, keyin panelga kirish
 * ruxsati bormi.
 *
 * MUHIM: bu faqat INTERFEYS qorovuli. Haqiqiy cheklov backendda, har bir
 * `/admin/*` endpointida — brauzerdagi tekshiruvni istalgan odam
 * DevTools bilan chetlab o'tadi.
 *
 * Kirish sahifasi bu yerda YO'Q: sessiya `.qahvadosh.uz` cookie'sida
 * (ADR 0004) va u web loyihasidagi Telegram oqimi orqali olinadi.
 * Ikkinchi kirish oqimini yozish — ikkita saqlanadigan joy va ikkita
 * buziladigan nuqta degani.
 */
export function AuthGuard({ children }: ChildrenProps) {
  const { t } = useTranslation();
  const setUser = useSessionStore(state => state.setUser);
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: QK.me,
    queryFn: async () => {
      try {
        return await authApi.me();
      } catch (err) {
        // 401/403 — kirilmagan. Bu xato emas, holat.
        if (isApiError(err) && (err.status === 401 || err.status === 403)) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (isLoading) return;
    setUser(user ?? null);
  }, [user, isLoading, setUser]);

  // Sessiya tugaganda (refresh ham rad etildi) keshni tozalaymiz va
  // `me` ni bo'sh qilamiz — shunda shu komponent kirish ekranini
  // ko'rsatadi. Aks holda panel eski kesh bilan "ishlayotgandek"
  // ko'rinib, har bir so'rov jimgina 401 bilan qaytardi.
  useEffect(() => {
    onSessionLost(() => {
      queryClient.setQueryData(QK.me, null);
      queryClient.removeQueries({ predicate: query => query.queryKey !== QK.me });
    });

    return () => onSessionLost(null);
  }, [queryClient]);

  if (isLoading) return <PageLoader />;

  // Backend yotgan bo'lsa ham nima bo'lganini aytamiz — bo'sh ekran emas.
  if (isError && !isApiError(error)) {
    return (
      <AuthNotice
        tone="warning"
        icon={WifiOff}
        title={t("errors.NETWORK_ERROR")}
        body={t("auth.networkHint")}
        action={
          <Button size="lg" variant="outline" onClick={() => window.location.reload()}>
            {t("auth.retry")}
          </Button>
        }
      />
    );
  }

  if (!user) {
    return (
      <AuthNotice
        icon={KeyRound}
        title={t("auth.signInRequired")}
        body={t("auth.signInHint")}
        steps={[t("auth.stepOne"), t("auth.stepTwo"), t("auth.stepThree")]}
        action={
          <Button asChild size="lg">
            <a href={`${env.VITE_WEB_URL}/kirish`}>{t("auth.goToLogin")}</a>
          </Button>
        }
        footnote={t("auth.sessionNote")}
      />
    );
  }

  // Rol bo'yicha emas, RUXSAT bo'yicha (ADR 0009): rollar ish paytida
  // yaratiladi va kod ularning nomini bilmaydi. Avval bu yerda
  // `ADMIN_ROLES.includes(role)` turardi.
  if (!canAccessAdmin(user)) {
    return (
      <AuthNotice
        tone="danger"
        icon={ShieldAlert}
        title={t("auth.forbiddenTitle")}
        body={t("auth.forbiddenHint")}
        action={
          <Button asChild size="lg" variant="outline">
            <a href={env.VITE_WEB_URL}>{t("auth.backToSite")}</a>
          </Button>
        }
      />
    );
  }

  return <>{children}</>;
}
